import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _debounce2 from "lodash/debounce";
function ownKeys(e, r) {
  var t = Object.keys(e);
  if (Object.getOwnPropertySymbols) {
    var o = Object.getOwnPropertySymbols(e);
    r && (o = o.filter(function (r) {
      return Object.getOwnPropertyDescriptor(e, r).enumerable;
    })), t.push.apply(t, o);
  }
  return t;
}
function _objectSpread(e) {
  for (var r = 1; r < arguments.length; r++) {
    var t = null != arguments[r] ? arguments[r] : {};
    r % 2 ? ownKeys(Object(t), !0).forEach(function (r) {
      _defineProperty(e, r, t[r]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) {
      Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r));
    });
  }
  return e;
}
import React, { useEffect, createRef, useMemo, useContext } from 'react';
import ReactHtmlParser from 'react-html-parser';
import classnames from 'classnames';
import { AnalyticsContext, AnalyticsLocationConsumer, trackEvent } from 'latitude-analytics';
import { Box } from '@latitude/box';
import SvgInline from '@latitude/svg-inline';
import { Heading4 } from '@latitude/heading';
import { Text } from '@latitude/text';
import { ALIGN, BREAKPOINT, COLOR, MARGIN } from '@latitude/core/utils/constants';
import { pxAsNumber } from '@latitude/core/helpers';
import { List, ListItem } from '@latitude/list';
import { Card, CardIcon, CardHeading, CardBody, Section, Slider } from './FeaturesSlider.styled';
const responsiveConfig = [{
  breakpoint: pxAsNumber(BREAKPOINT.LG),
  settings: {
    slidesToShow: 3
  }
}, {
  breakpoint: pxAsNumber(BREAKPOINT.MD),
  settings: {
    slidesToShow: 2
  }
}, {
  breakpoint: pxAsNumber(BREAKPOINT.SM),
  settings: 'unslick' // hides slick when breakpoint is smaller than SM
}];
const FeaturesSlider = _ref => {
  let {
    id,
    className,
    data = [],
    arrows = false,
    dots = true,
    infinite = false,
    speed = 300,
    initialSlide = 0,
    slidesToScroll = 1,
    slidesToShow = 4,
    responsive = responsiveConfig,
    backgroundColor = COLOR.TRANSPARENT,
    whiteTiles = false,
    heading,
    subheading
  } = _ref;
  const {
    0: analytics
  } = useContext(AnalyticsContext);
  const cardsRef = useMemo(() => {
    const refs = {};
    data.forEach((card, index) => {
      refs[card.id || index] = /*#__PURE__*/createRef();
    });
    return refs;
  }, [data]);
  const setTitleHeight = height => {
    Object.keys(cardsRef).forEach(key => {
      const ref = cardsRef[key];
      if (ref && ref.current) {
        ref.current.style.minHeight = height ? `${height}px` : "auto";
      }
    });
  };
  const equalizeHeadingHeight = () => {
    let max = 0;
    Object.keys(cardsRef).forEach(key => {
      var _cardsRef$key;
      const element = (_cardsRef$key = cardsRef[key]) === null || _cardsRef$key === void 0 ? void 0 : _cardsRef$key.current;
      if (element && element.offsetHeight > max) {
        max = element.offsetHeight;
      }
    });
    setTitleHeight(max);
  };
  const handleWindowResize = () => {
    setTitleHeight(); // resets height(s) to 'auto'
    equalizeHeadingHeight();
  };
  const debouncedHandleResize = _debounce2(handleWindowResize, 100);
  useEffect(() => {
    equalizeHeadingHeight();
    window.addEventListener('resize', debouncedHandleResize);
    return () => window.removeEventListener('resize', debouncedHandleResize);
  }, [data, cardsRef]);
  const calculatedSlidesToShow = slidesToShow > data.length ? data.length : slidesToShow;
  const calculatedResponsive = responsive.map(config => _objectSpread(_objectSpread({}, config), {}, {
    settings: config.settings === 'unslick' ? {
      slidesToShow: data.length
    } : _objectSpread(_objectSpread({}, config.settings), {}, {
      slidesToShow: config.settings.slidesToShow > data.length ? data.length : config.settings.slidesToShow
    })
  }));
  return /*#__PURE__*/React.createElement(Section, {
    id: id,
    className: classnames('lfs-features-slider', className),
    backgroundColor: backgroundColor
  }, /*#__PURE__*/React.createElement(Box, {
    isResponsive: true,
    margin: "0 auto",
    className: "lfs-box-responsive"
  }, heading && /*#__PURE__*/React.createElement(Heading4, {
    className: "lfs-heading",
    align: ALIGN.CENTER,
    color: COLOR.BLACK,
    marginBottom: subheading ? MARGIN.M16 : MARGIN.M32
  }, ReactHtmlParser(heading)), subheading && /*#__PURE__*/React.createElement(Box, {
    marginBottom: MARGIN.M32,
    className: "lfs-subheading"
  }, /*#__PURE__*/React.createElement(Text, {
    align: ALIGN.CENTER,
    color: COLOR.BLACK
  }, subheading)), /*#__PURE__*/React.createElement(AnalyticsLocationConsumer, null, location => /*#__PURE__*/React.createElement(Slider, {
    arrows: arrows,
    dots: dots,
    infinite: infinite,
    initialSlide: initialSlide,
    slidesToScroll: slidesToScroll,
    slidesToShow: calculatedSlidesToShow,
    speed: speed,
    responsive: calculatedResponsive,
    whiteTiles: whiteTiles,
    afterChange: slideNumber => {
      // Get viewport
      const viewportWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0);

      // Find which responsive breakpoint to determine number of slides in view
      // Then create label concatenating title from each slide
      let label = '';
      for (let i = responsiveConfig.length - 1; i >= 0; i--) {
        const config = responsiveConfig[i];
        if (viewportWidth <= config.breakpoint) {
          const slidesInViewport = data.slice(slideNumber, slideNumber + config.settings.slidesToShow);
          label = slidesInViewport.map(slide => slide.title).join('::');
          break;
        }
      }
      trackEvent(analytics, {
        category: 'slider',
        action: 'interaction',
        label,
        location: heading
      });
    }
  }, data.map((card, i) => /*#__PURE__*/React.createElement(Card, {
    key: i,
    className: classnames('lfs-card', {
      'white-tiles': whiteTiles
    })
  }, card.icon && /*#__PURE__*/React.createElement(CardIcon, {
    className: "lfs-card-icon"
  }, /*#__PURE__*/React.createElement(SvgInline, {
    className: "h-100 w-100",
    name: card.icon
  })), /*#__PURE__*/React.createElement("div", {
    ref: cardsRef[card.id || i]
  }, /*#__PURE__*/React.createElement(CardHeading, {
    className: "lfs-card-heading"
  }, card.title)), /*#__PURE__*/React.createElement(CardBody, {
    className: "lfs-card-body"
  }, card.text && /*#__PURE__*/React.createElement(Text, {
    align: ALIGN.CENTER,
    marginBottom: MARGIN.M16
  }, ReactHtmlParser(card.text)), card.list && /*#__PURE__*/React.createElement(Text, null, /*#__PURE__*/React.createElement(List, {
    dotColor: COLOR.BLUE_LIGHT,
    marginLeft: MARGIN.M32
  }, card.list.map(item => /*#__PURE__*/React.createElement(ListItem, null, ReactHtmlParser(item))))), card.jsx)))))));
};
export default FeaturesSlider;